import type { AuthErrorType } from '../types/authError';

const FAILED_FETCH_MESSAGES = [
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
];

const FIRESTORE_ERROR_TYPES = new Map<string, AuthErrorType>([
  ['permission-denied', 'firebase-auth'],
  ['unavailable', 'network'],
  ['deadline-exceeded', 'timeout'],
]);

const firestoreErrorType = (error: unknown): AuthErrorType | undefined =>
  error instanceof Error && 'code' in error && typeof error.code === 'string'
    ? FIRESTORE_ERROR_TYPES.get(error.code)
    : undefined;

/**
 * Classifies authentication errors into categories for appropriate error handling
 * @param error - The error to classify
 * @returns Classified error type
 */
export const classifyAuthErrorType = (error: unknown): AuthErrorType => {
  // Firestore errors, by their unprefixed `code`
  const firestoreType = firestoreErrorType(error);
  if (firestoreType !== undefined) {
    return firestoreType;
  }

  // Network/DNS failures: Chromium, Safari, Firefox
  if (
    error instanceof TypeError &&
    FAILED_FETCH_MESSAGES.some(message => error.message.includes(message))
  ) {
    return 'network';
  }

  // Timeout errors
  if (
    (error instanceof DOMException && error.name === 'AbortError') ||
    (error instanceof Error && error.message.includes('timeout'))
  ) {
    return 'timeout';
  }

  // Service downtime (502, 503)
  if (
    error instanceof Error &&
    (error.message.includes('502') || error.message.includes('503'))
  ) {
    return 'service-down';
  }

  // Token exchange specific errors
  if (
    error instanceof Error &&
    error.message.toLowerCase().includes('token exchange')
  ) {
    return 'token-exchange';
  }

  // Firebase authentication errors
  if (
    error instanceof Error &&
    (error.message.includes('Firebase') ||
      error.message.includes('custom token') ||
      error.message.includes('auth/'))
  ) {
    return 'firebase-auth';
  }

  // Unknown errors
  return 'unknown';
};
